/**
 * Wires a SessionStore into assistant-ui's external-store runtime and exposes
 * the bridge-level actions (permissions, interrupt, model switch) to the
 * component tree through a context.
 */

import {
  type AppendMessage,
  type ThreadMessageLike,
  useExternalStoreRuntime,
} from '@assistant-ui/react'
import type { Command, SessionRecord } from '@web/api/types'
import { acpMessages } from '@web/state/acp'
import type { BridgeState, PermissionRequest } from '@web/state/bridge'
import type { SessionStore, Snapshot } from '@web/state/session-store'
import { timelineMessages } from '@web/state/timeline'
import { useTimelinePreferences } from '@web/state/timeline-preferences'
import { describeWork } from '@web/state/working'
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useSyncExternalStore,
} from 'react'

export type PermissionDecision = { optionId: string } | { cancelled: true }

export type BridgeActions = {
  sessionId: string
  snapshot: Snapshot
  acceptSessionRecord: (record: SessionRecord) => void
  reportError: (error: string, command?: string) => void
  send: (cmd: Command) => boolean
  queueStartupMessage: (cmd: Extract<Command, { type: 'queue' }>) => boolean
  deferStartupOption: (configId: string, value: string | boolean) => boolean
  removeStartupMessage: (id: string) => void
  respondToPermission: (requestId: string, decision: PermissionDecision) => void
  interrupt: () => void
  permissionForToolUse: (toolUseId: string) => PermissionRequest | null
}

const BridgeContext = createContext<BridgeActions | null>(null)

export function useBridge(): BridgeActions {
  const ctx = useContext(BridgeContext)
  if (!ctx) throw new Error('useBridge must be used inside BridgeProvider')
  return ctx
}

export function useOptionalBridge(): BridgeActions | null {
  return useContext(BridgeContext)
}

export function useSnapshot(store: SessionStore): Snapshot {
  return useSyncExternalStore(
    store.subscribe,
    store.getSnapshot,
    store.getSnapshot,
  )
}

function decisionToCommand(
  req: PermissionRequest,
  decision: PermissionDecision,
): Command {
  return {
    type: 'permission',
    request_id: req.requestId,
    ...('optionId' in decision
      ? { option_id: decision.optionId }
      : { outcome: 'cancelled' as const }),
  }
}

/** Provide lifecycle and event actions without constructing a chat runtime. */
export function useBridgeActions(store: SessionStore, snapshot: Snapshot) {
  const respondToPermission = useCallback(
    (requestId: string, decision: PermissionDecision) => {
      const req = store.getSnapshot().bridge.permissions[requestId]
      if (!req || req.resolved) return
      store.send(decisionToCommand(req, decision))
    },
    [store],
  )

  return useMemo<BridgeActions>(
    () => ({
      sessionId: store.sessionId,
      snapshot,
      acceptSessionRecord: store.acceptSessionRecord,
      send: store.send,
      reportError: store.reportError,
      queueStartupMessage: store.queueStartupMessage,
      deferStartupOption: store.deferStartupOption,
      removeStartupMessage: store.removeStartupMessage,
      respondToPermission,
      interrupt: () => {
        // A Stop sent while the socket is reconnecting would otherwise vanish.
        if (!store.send({ type: 'interrupt' }))
          store.reportError(
            'Stop did not reach the session because the connection is reconnecting. Try again in a moment.',
            'interrupt',
          )
      },
      permissionForToolUse: (toolUseId) => {
        const b = store.getSnapshot().bridge
        const id = b.permissionByToolUse[toolUseId]
        return id ? (b.permissions[id] ?? null) : null
      },
    }),
    [store, snapshot, respondToPermission],
  )
}

export function useBridgeRuntime(store: SessionStore) {
  const snapshot = useSnapshot(store)
  const preferences = useTimelinePreferences()

  const messages = useMemo(
    () =>
      timelineMessages(
        acpMessages(store.getDisplayState(snapshot), snapshot.bridge),
        preferences,
      ).map((message) =>
        store.isOptimistic(
          message.metadata?.custom?.requestId as string | undefined,
        )
          ? {
              ...message,
              metadata: {
                ...message.metadata,
                custom: {
                  ...message.metadata?.custom,
                  delivered: false,
                  pending: true,
                },
              },
            }
          : message,
      ),
    [snapshot, store, preferences],
  )

  const bridge: BridgeState = snapshot.bridge
  const work = describeWork(snapshot.acp, bridge)
  const running = !snapshot.hydrating && work.working
  const canSend =
    bridge.record?.migration_status !== 'diagnostic' &&
    !snapshot.hydrating &&
    ![
      'error',
      'exited',
      'detached',
      'asleep',
      'closed',
      'starting',
      'unknown',
    ].includes(bridge.status) &&
    snapshot.socket === 'open'

  const actions = useBridgeActions(store, snapshot)
  const respondToPermission = actions.respondToPermission

  const onNew = useCallback(
    async (message: AppendMessage) => {
      const text = message.content
        .filter((p): p is { type: 'text'; text: string } => p.type === 'text')
        .map((p) => p.text)
        .join('\n')
      if (!text.trim()) return
      if (!store.send({ type: 'prompt', text }))
        throw new Error('Session is not connected.')
    },
    [store],
  )

  const onCancel = useCallback(async () => {
    store.send({ type: 'interrupt' })
  }, [store])

  const onRespondToToolApproval = useCallback(
    ({
      approvalId,
      approved,
      optionId,
    }: {
      approvalId: string
      approved: boolean
      optionId?: string
      reason?: string
    }) => {
      const request = store.getSnapshot().bridge.permissions[approvalId]
      const selected =
        request?.options?.find((option) => option.optionId === optionId) ??
        request?.options?.find((option) =>
          approved
            ? option.kind === 'allow_once'
            : option.kind === 'reject_once',
        )
      if (selected)
        respondToPermission(approvalId, { optionId: selected.optionId })
    },
    [respondToPermission, store],
  )

  const runtime = useExternalStoreRuntime<ThreadMessageLike>({
    messages,
    convertMessage: (m) => m,
    isRunning: running,
    isSendDisabled: !canSend,
    onNew,
    onCancel,
    onRespondToToolApproval,
  })

  return { runtime, actions, snapshot }
}

export const BridgeProvider = BridgeContext.Provider
